<script>
    import { onMount, onDestroy } from 'svelte';
    import { Mixer } from '../../Mixer.js';
    import { BuildSettings, OutputConfig } from '../../OutputConfig.js';
    import { BrowserHandler } from '../../BrowserHandler.js';
    import { video_suspended } from '../../stores.js';
    import { MeterLoop } from '../MeterLoop.js';

    export let isMuted = false;
    export let peerId = undefined;
    export let meterSource;
    export let peerType = undefined;
    export let channelNum = -1;

    // --- Layout ---
    export let width = null;
    export let height = null;
    export let orientation = "vertical";
    export let barWidth = 5;
    export let barGap = 3;
    export let barRadius = 2;
    export let paddingTop = 4;
    export let paddingRight = 2;
    export let paddingBottom = 4;
    export let paddingLeft = 2;

    // --- Ballistics ---
    export let style = "ppm";
    export let attackMs = null;
    export let releaseMs = null;

    // --- Peak hold indicator ---
    export let showPeakHold = true;
    export let peakHoldMs = 1500;
    export let peakFallDbPerSec = 20;

    // --- Colors ---
    export let colors = {
        background: "rgba(40, 43, 54, 0.2)",
        low:        "rgba(85, 155, 95, 0.5)",
        mid:        "rgba(204, 183, 85, 0.5)",
        high:       "rgba(220, 85, 85, 0.6)",
        tint:       "rgba(190, 157, 134, 0.15)",
        peak:       "rgba(255, 255, 255, 0.4)"
    };

    export let channelCount = null;

    const PRESETS = {
        fast:      { attackMs: 30,  releaseMs: 300 },
        ppm:       { attackMs: 40,  releaseMs: 600 },
        vu:        { attackMs: 300, releaseMs: 300 },
        bs1770ish: { attackMs: 400, releaseMs: 1000 }
    };

    const DB_RANGE = 60;

    let canvas;
    let ctx;
    let dpr = 1;
    let cssW = 0;
    let cssH = 0;
    let resizeObserver = null;

    let levels = new Float64Array(0);
    let peaks  = new Float64Array(0);
    let peakTimes = new Float64Array(0);
    let rawLevels = new Float32Array(0);
    let attackTau = PRESETS.ppm.attackMs / 1000;
    let releaseTau = PRESETS.ppm.releaseMs / 1000;

    // -- per channel, the fill and peak the last draw showed (device pixels, peak -1 = hidden):
    // -- a frame that would draw the same pixels is skipped, so a meter at rest costs nothing
    let shown = new Int32Array(0);
    let dirty = true;
    let gradient = null;    // one gradient spans every bar: they share the fill axis

    const clamp01 = x => Math.max(0, Math.min(1, x));

    function resolveChannelCount() {
        if (channelCount !== null) return channelCount;
        switch (meterSource) {
            case "stream-sum": return 2;
            case "mic-input":  return 1;
            case "stream-ch":  return 1;
            default:           return 1;
        }
    }

    function readLevels(out) {
        switch (meterSource) {
            case "stream-sum": {
                const l = Mixer.getStreamSumLevel(out);
                if (!l) return false;
                return true;
            }
            case "mic-input": {
                const l = Mixer.getConfInputLevel(peerId, out);
                if (!l) return false;
                return true;
            }
            case "stream-ch": {
                if (channelNum < 0) return false;
                out[0] = Mixer.getRemoteTalentChannelLevel(peerId, channelNum);
                return true;
            }
        }
        return false;
    }

    function allocChannels(n) {
        if (levels.length === n) return;
        levels = new Float64Array(n);
        peaks = new Float64Array(n);
        peakTimes = new Float64Array(n);
        rawLevels = new Float32Array(n);
        shown = new Int32Array(2 * n);
        dirty = true;
    }

    function updateLevelsAndPeaks(now, dt) {
        const n = levels.length;
        if (!readLevels(rawLevels)) return;

        const fallPerFrame = (peakFallDbPerSec / DB_RANGE) * dt;

        for (let i = 0; i < n; i++) {
            const target = Math.max(0, Math.min(1, rawLevels[i]));
            const tau = target > levels[i] ? attackTau : releaseTau;
            const alpha = tau > 0 ? 1 - Math.exp(-dt / tau) : 1;
            levels[i] += alpha * (target - levels[i]);

            if (showPeakHold) {
                if (levels[i] >= peaks[i]) {
                    peaks[i] = levels[i];
                    peakTimes[i] = now;
                } else if (now - peakTimes[i] > peakHoldMs) {
                    peaks[i] = Math.max(levels[i], peaks[i] - fallPerFrame);
                }
            }
        }
    }

    function resizeCanvas() {
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        cssW = Math.max(1, rect.width);
        cssH = Math.max(1, rect.height);
        dpr = window.devicePixelRatio || 1;
        canvas.width = Math.round(cssW * dpr);
        canvas.height = Math.round(cssH * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        invalidate();
    }

    // -- the canvas was cleared, or something other than a level changes the picture
    function invalidate() {
        gradient = null;
        dirty = true;
    }

    function roundRectPath(x, y, w, h, r) {
        const rr = Math.max(0, Math.min(r, w / 2, h / 2));
        ctx.beginPath();
        ctx.moveTo(x + rr, y);
        ctx.arcTo(x + w, y,     x + w, y + h, rr);
        ctx.arcTo(x + w, y + h, x,     y + h, rr);
        ctx.arcTo(x,     y + h, x,     y,     rr);
        ctx.arcTo(x,     y,     x + w, y,     rr);
        ctx.closePath();
    }

    function draw() {
        if (!ctx) return;
        const vert = orientation === "vertical";
        const contentW = Math.max(0, cssW - paddingLeft - paddingRight);
        const contentH = Math.max(0, cssH - paddingTop - paddingBottom);
        if (!settle(vert ? contentH : contentW)) return;

        ctx.clearRect(0, 0, cssW, cssH);
        const n = levels.length;
        if (n === 0 || contentW <= 0 || contentH <= 0) return;

        if (!gradient) {
            gradient = vert
                ? ctx.createLinearGradient(0, paddingTop + contentH, 0, paddingTop)
                : ctx.createLinearGradient(paddingLeft, 0, paddingLeft + contentW, 0);
            gradient.addColorStop(0.0, colors.low);
            gradient.addColorStop(0.6, colors.mid);
            gradient.addColorStop(1.0, colors.high);
        }

        ctx.save();
        if (isMuted) ctx.globalAlpha = 0.55;

        const totalGap = (n - 1) * barGap;
        if (vert) {
            const available = contentW - totalGap;
            const bw = Math.min(barWidth, Math.max(1, available / n));
            const used = n * bw + totalGap;
            const startX = paddingLeft + (contentW - used) / 2;
            for (let i = 0; i < n; i++) {
                const x = startX + i * (bw + barGap);
                drawBar(x, paddingTop, bw, contentH, i, true);
            }
        } else {
            const available = contentH - totalGap;
            const bh = Math.min(barWidth, Math.max(1, available / n));
            const used = n * bh + totalGap;
            const startY = paddingTop + (contentH - used) / 2;
            for (let i = 0; i < n; i++) {
                const y = startY + i * (bh + barGap);
                drawBar(paddingLeft, y, contentW, bh, i, false);
            }
        }
        ctx.restore();
    }

    // -- records this frame's fill and peak per channel, in device pixels along the fill axis;
    // -- false when the last draw showed the same
    function settle(len) {
        let moved = dirty;
        dirty = false;
        for (let i = 0; i < levels.length; i++) {
            const fill = Math.round(clamp01(levels[i]) * len * dpr);
            const peak = showPeakHold && peaks[i] > 0.001 ? Math.round(clamp01(peaks[i]) * len * dpr) : -1;
            if (fill === shown[2 * i] && peak === shown[2 * i + 1]) continue;
            shown[2 * i] = fill;
            shown[2 * i + 1] = peak;
            moved = true;
        }
        return moved;
    }

    function drawBar(x, y, w, h, i, vert) {
        ctx.fillStyle = colors.background;
        roundRectPath(x, y, w, h, barRadius);
        ctx.fill();

        ctx.save();
        roundRectPath(x, y, w, h, barRadius);
        ctx.clip();

        const fillLen = shown[2 * i] / dpr;
        ctx.fillStyle = gradient;
        if (vert) ctx.fillRect(x, y + h - fillLen, w, fillLen);
        else      ctx.fillRect(x, y, fillLen, h);

        if (colors.tint) {
            ctx.fillStyle = colors.tint;
            ctx.fillRect(x, y, w, h);
        }

        const peakLen = shown[2 * i + 1] / dpr;
        if (peakLen >= 0) {
            ctx.fillStyle = colors.peak;
            if (vert) ctx.fillRect(x, y + h - peakLen - 1, w, 2);
            else      ctx.fillRect(x + peakLen - 1, y, 2, h);
        }

        ctx.restore();
    }

    // -- driven by MeterLoop, shared by every meter on the page
    function frame(now, dt) {
        updateLevelsAndPeaks(now, dt);
        draw();
    }

    onMount(() => {
        // -- manual BuildSettings kill-switch: leaving ctx unset keeps every draw path below inert
        // -- (frame never joins the loop, applyMeterSuspend and draw bail on !ctx)
        if (BuildSettings.DISABLE_AUDIO_METERS) return;
        ctx = canvas.getContext("2d");
        allocChannels(resolveChannelCount());
        resizeCanvas();

        resizeObserver = new ResizeObserver(() => {
            resizeCanvas();
            draw();
        });
        resizeObserver.observe(canvas);

        MeterLoop.intervalMs = BrowserHandler.audio_meter_interval_ms(OutputConfig.session.browser);

        // -- don't start while video is suspended (backgrounded on iOS); the reactive below
        // -- starts it on resume. Meters are pure visualization -- audio is unaffected either way.
        if (!$video_suspended)
            MeterLoop.add(frame);
    });

    onDestroy(() => {
        MeterLoop.remove(frame);
        if (resizeObserver) resizeObserver.disconnect();
    });

    // -- main-thread meter suspend (backgrounded on iOS): leave/rejoin the draw loop. The !ctx
    // -- guard skips the initial pre-mount run so onMount owns the first start.
    $: applyMeterSuspend($video_suspended);

    function applyMeterSuspend(suspended){
        if (!ctx) return;
        if (suspended) MeterLoop.remove(frame);
        else MeterLoop.add(frame);
    }

    // -- any prop can change the picture without moving a level
    $: $$props, invalidate();

    $: n = channelCount !== null
        ? channelCount
        : (meterSource === "stream-sum" ? 2 : 1);

    $: {
        const p = PRESETS[style] || PRESETS.ppm;
        attackTau = (attackMs ?? p.attackMs) / 1000;
        releaseTau = (releaseMs ?? p.releaseMs) / 1000;
    }

    $: if (canvas) allocChannels(n);

    $: intrinsicMinor = n * barWidth + Math.max(0, n - 1) * barGap + paddingLeft + paddingRight;
    $: styleWidth  = width  !== null ? `${width}px`
                   : (orientation === "vertical"   ? `${intrinsicMinor}px` : "100%");
    $: styleHeight = height !== null ? `${height}px`
                   : (orientation === "horizontal" ? `${intrinsicMinor}px` : "100%");
</script>

<div
    class="meter-container"
    class:muted={isMuted}
    style:width={styleWidth}
    style:height={styleHeight}
>
    <canvas bind:this={canvas}></canvas>
</div>

<style lang="scss">
    .meter-container {
        display: inline-block;
        line-height: 0;
        vertical-align: middle;
    }

    canvas {
        display: block;
        width: 100%;
        height: 100%;
    }
</style>
